import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Eye, EyeOff, Keyboard, Lock, ShieldCheck, UserRound, X } from "lucide-react";
import CaixaLogo from "./CaixaLogo";
import SecureLoginOverlay from "./SecureLoginOverlay";
import PasswordResetModal from "./PasswordResetModal";
import VirtualKeyboard from "./VirtualKeyboard";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const LoginModal = ({ open, onOpenChange, hideCloseButton = false, standalone = false }) => {
  const [showPwd, setShowPwd] = useState(false);
  const [id, setId] = useState("");
  const [pwd, setPwd] = useState("");
  const [securing, setSecuring] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'id' or 'pwd'
  const navigate = useNavigate();
  const { login } = useAuth();

  const openKeyboard = (field) => {
    setActiveField(field);
    setKeyboardOpen(true);
  };

  const handleVirtualKeyPress = (key) => {
    if (key === 'Backspace') {
      if (activeField === 'id') setId(prev => prev.slice(0, -1));
      else if (activeField === 'pwd') setPwd(prev => prev.slice(0, -1));
    } else if (key === 'Enter') {
      setKeyboardOpen(false);
    } else {
      if (activeField === 'id') setId(prev => prev + key);
      else if (activeField === 'pwd') setPwd(prev => prev + key);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!id || !pwd) {
      toast.error("Veuillez renseigner votre identifiant et votre mot de passe.");
      return;
    }
    setSecuring(true);

    const start = Date.now();
    let user = null;
    let errMsg = null;
    try {
      user = await login(id.trim(), pwd);
    } catch (err) {
      errMsg = err?.response?.data?.detail || "Identifiant ou mot de passe incorrect. Veuillez réessayer.";
    }

    // Enforce minimum 3-second secure animation for the banking feel
    const elapsed = Date.now() - start;
    const remaining = Math.max(0, 3000 - elapsed);
    setTimeout(() => {
      setSecuring(false);
      if (errMsg) {
        toast.error(errMsg);
        return;
      }
      // Success → redirect by role and close modal
      onOpenChange(false);
      setId("");
      setPwd("");
      if (user?.role === "admin") navigate("/admin");
      else navigate("/dashboard");
      toast.success(`Bienvenue${user?.first_name ? ", " + user.first_name : ""} !`);
    }, remaining);
  };

  return (
    <>
      <SecureLoginOverlay show={securing} />
      
      {standalone ? (
        /* Mode standalone - Sans Dialog wrapper, pour LoginPage */
        <div className="w-full max-w-[480px] bg-white shadow-2xl rounded-none sm:rounded-md overflow-hidden">
          {/* Header bar */}
          <div className="bg-[#007EAE] text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center">
              <CaixaLogo size={30} variant="white" />
            </div>
          </div>

          <div className="sr-only">Accès client CaixaBank</div>
          <div className="sr-only">
            Connectez-vous à votre espace client CaixaBank.
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            <h2 className="text-[19px] sm:text-[22px] font-semibold text-slate-800 leading-tight" style={{ fontFamily: "Georgia, serif" }}>
              Accédez à votre espace CaixaBank
            </h2>
            <p className="text-slate-500 text-[13px] sm:text-sm mt-1">
              Saisissez vos identifiants pour continuer.
            </p>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              {/* Identifier */}
              <div>
                <label className="text-[13px] font-medium text-slate-700">Identifiant</label>
                <div className="mt-1 relative">
                  <UserRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={id}
                    onChange={(e) => setId(e.target.value)}
                    placeholder="Votre identifiant"
                    autoComplete="username"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-[13px] font-medium text-slate-700">Mot de passe</label>
                <div className="mt-1 relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    className="w-full pl-9 pr-10 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    aria-label={showPwd ? "Masquer" : "Afficher"}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[12.5px]">
                <button 
                  type="button" 
                  onClick={() => setResetModalOpen(true)}
                  className="text-[#007EAE] hover:underline text-left"
                >
                  Avez-vous oublié vos codes d&apos;accès ?
                </button>
                <button 
                  type="button" 
                  onClick={() => openKeyboard('pwd')}
                  className="text-[#007EAE] hover:underline inline-flex items-center gap-1 text-left"
                >
                  <Keyboard className="w-3.5 h-3.5" /> Clavier virtuel
                </button>
              </div>

              <button
                type="submit"
                disabled={securing}
                className="w-full bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold py-3 btn-press disabled:opacity-70 rounded-none"
              >
                {securing ? "Connexion en cours…" : "Entrer"}
              </button>
            </form>

            <div className="mt-4 flex items-start gap-2 p-3 bg-slate-50 border border-slate-200">
              <ShieldCheck className="w-4 h-4 text-[#007EAE] mt-0.5 shrink-0" />
              <p className="text-[12px] text-slate-600 leading-relaxed">
                Connexion sécurisée par chiffrement. Ne partagez jamais vos identifiants. CaixaBank ne vous demandera jamais votre mot de passe par e-mail ou téléphone.
              </p>
            </div>

            <div className="mt-4 text-center text-[12.5px] text-slate-600">
              Vous n&apos;êtes pas encore client ?{" "}
              <a
                href="https://www.caixabank.es/particular/holabank/devenir-client.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#007EAE] font-semibold hover:underline"
              >
                Ouvrir un compte
              </a>
            </div>
          </div>
        </div>
      ) : (
        /* Mode Dialog - Avec Dialog wrapper, pour utilisation normale */
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            className="p-0 w-[calc(100vw-1.5rem)] sm:max-w-[480px] max-h-[92vh] overflow-y-auto overflow-x-hidden border-0 shadow-2xl rounded-none sm:rounded-md"
          >
            {/* Header bar (sticky on mobile) */}
            <div className="sticky top-0 z-10 bg-[#007EAE] text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center">
                <CaixaLogo size={30} variant="white" />
              </div>
              {!hideCloseButton && (
                <button
                  onClick={() => onOpenChange(false)}
                  className="text-white/90 hover:text-white p-1"
                  aria-label="Fermer"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <DialogTitle className="sr-only">Accès client CaixaBank</DialogTitle>
            <DialogDescription className="sr-only">
              Connectez-vous à votre espace client CaixaBank.
            </DialogDescription>

            {/* Body */}
            <div className="px-5 sm:px-7 py-6">
              <h2 className="text-[19px] sm:text-[22px] font-semibold text-slate-800 leading-tight" style={{ fontFamily: "Georgia, serif" }}>
                Accédez à votre espace CaixaBank
              </h2>
              <p className="text-slate-500 text-[13px] sm:text-sm mt-1">
                Saisissez vos identifiants pour continuer.
              </p>

              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                {/* Identifier */}
                <div>
                  <label className="text-[13px] font-medium text-slate-700">Identifiant</label>
                  <div className="mt-1 relative">
                    <UserRound className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={id}
                      onChange={(e) => setId(e.target.value)}
                      placeholder="Votre identifiant"
                      autoComplete="username"
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="text-[13px] font-medium text-slate-700">Mot de passe</label>
                  <div className="mt-1 relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPwd ? "text" : "password"}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      placeholder="Votre mot de passe"
                      autoComplete="current-password"
                      className="w-full pl-9 pr-10 py-2.5 border border-slate-300 focus:border-[#007EAE] focus:ring-2 focus:ring-[#007EAE]/20 outline-none text-[15px] rounded-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      aria-label={showPwd ? "Masquer" : "Afficher"}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[12.5px]">
                  <button 
                    type="button" 
                    onClick={() => setResetModalOpen(true)}
                    className="text-[#007EAE] hover:underline text-left"
                  >
                    Avez-vous oublié vos codes d&apos;accès ?
                  </button>
                  <button 
                    type="button" 
                    onClick={() => openKeyboard('pwd')}
                    className="text-[#007EAE] hover:underline inline-flex items-center gap-1 text-left"
                  >
                    <Keyboard className="w-3.5 h-3.5" /> Clavier virtuel
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={securing}
                  className="w-full bg-[#007EAE] hover:bg-[#006c97] text-white font-semibold py-3 btn-press disabled:opacity-70 rounded-none"
                >
                  {securing ? "Connexion en cours…" : "Entrer"}
                </button>
              </form>

              <div className="mt-4 flex items-start gap-2 p-3 bg-slate-50 border border-slate-200">
                <ShieldCheck className="w-4 h-4 text-[#007EAE] mt-0.5 shrink-0" />
                <p className="text-[12px] text-slate-600 leading-relaxed">
                  Connexion sécurisée par chiffrement. Ne partagez jamais vos identifiants. CaixaBank ne vous demandera jamais votre mot de passe par e-mail ou téléphone.
                </p>
              </div>

              <div className="mt-4 text-center text-[12.5px] text-slate-600">
                Vous n&apos;êtes pas encore client ?{" "}
                <a
                  href="https://www.caixabank.es/particular/holabank/devenir-client.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#007EAE] font-semibold hover:underline"
                >
                  Ouvrir un compte
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      <PasswordResetModal open={resetModalOpen} onOpenChange={setResetModalOpen} />
      {keyboardOpen && (
        <VirtualKeyboard 
          onKeyPress={handleVirtualKeyPress} 
          onClose={() => setKeyboardOpen(false)} 
        />
      )}
    </>
  );
};

export default LoginModal;
